import { useCallback, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { getNativePlatform, isNativeShell } from '@/core/capacitor'

declare global {
  interface Window {
    __phoneLoginDebug?: (msg: string) => void
  }
}

type UsePhoneAuthDebugOptions = {
  enabled: boolean
}

const MAX_LOG_ENTRIES = 100

export function usePhoneAuthDebug({ enabled }: UsePhoneAuthDebugOptions) {
  const [debugLog, setDebugLog] = useState<string[]>([])
  const debugLogRef = useRef<string[]>([])
  const enabledRef = useRef(enabled)
  const isNative = isNativeShell()

  useEffect(() => {
    enabledRef.current = enabled
    if (enabled) {
      setDebugLog([...debugLogRef.current])
    }
  }, [enabled])

  const addDebugLog = useCallback((msg: string) => {
    const timestamp = new Date().toLocaleTimeString()
    const logEntry = `[${timestamp}] ${msg}`
    console.log('[PhoneLogin Debug]', logEntry)
    debugLogRef.current = [...debugLogRef.current.slice(-(MAX_LOG_ENTRIES - 1)), logEntry]
    if (enabledRef.current) {
      setDebugLog([...debugLogRef.current])
    }
  }, [])

  const clearDebugLog = useCallback(() => {
    debugLogRef.current = []
    setDebugLog([])
  }, [])

  const handleTestNative = useCallback(async () => {
    addDebugLog('=== Testing Native Plugin ===')
    try {
      addDebugLog(`FirebaseAuthentication exists: ${!!FirebaseAuthentication}`)
      const currentUser = await FirebaseAuthentication.getCurrentUser()
      addDebugLog(`getCurrentUser: ${JSON.stringify(currentUser)}`)
      addDebugLog('Attempting direct signInWithPhoneNumber...')
      const signInResult = await FirebaseAuthentication.signInWithPhoneNumber({
        phoneNumber: '+16025555555',
        skipNativeAuth: false,
      })
      addDebugLog(`signInWithPhoneNumber returned: ${JSON.stringify(signInResult)}`)
    } catch (err: any) {
      addDebugLog(`❌ Test error: ${err?.message || err}`)
      addDebugLog(`Error details: ${JSON.stringify(err)}`)
    }
  }, [addDebugLog])

  useEffect(() => {
    window.__phoneLoginDebug = addDebugLog
    return () => {
      delete window.__phoneLoginDebug
    }
  }, [addDebugLog])

  useEffect(() => {
    addDebugLog('=== PhoneLogin Mounted ===')
    addDebugLog(`Capacitor exists: ${typeof Capacitor !== 'undefined'}`)
    addDebugLog(`isNativePlatform(): ${Capacitor?.isNativePlatform?.()}`)
    addDebugLog(`getPlatform(): ${Capacitor?.getPlatform?.()}`)
    addDebugLog(`isNativeShell(): ${isNativeShell()}`)
    addDebugLog(`getNativePlatform(): ${getNativePlatform()}`)

    const hasWebkitBridge = !!(window as any).webkit?.messageHandlers?.bridge
    addDebugLog(`webkit.messageHandlers.bridge: ${hasWebkitBridge}`)

    const userAgent = navigator.userAgent
    addDebugLog(`UA contains Capacitor: ${/Capacitor/i.test(userAgent)}`)
    addDebugLog(`UA: ${userAgent.substring(0, 100)}...`)
  }, [addDebugLog])

  useEffect(() => {
    if (!isNative) {
      addDebugLog('⚠️ Not native, skipping native listeners')
      return
    }

    const handles: Array<{ remove: () => Promise<void> }> = []

    const setupListeners = async () => {
      try {
        handles.push(await FirebaseAuthentication.addListener('phoneAuthDebug' as any, (event: any) => {
          addDebugLog(`🔧 NATIVE: ${event.message || JSON.stringify(event)}`)
          if (event.details) {
            addDebugLog(`   Details: ${JSON.stringify(event.details)}`)
          }
        }))
        handles.push(await FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
          addDebugLog(`📱 phoneCodeSent: ${JSON.stringify(event)}`)
        }))
        handles.push(await FirebaseAuthentication.addListener('phoneVerificationFailed', (event) => {
          addDebugLog(`❌ phoneVerificationFailed: ${JSON.stringify(event)}`)
        }))
      } catch (err: any) {
        addDebugLog(`❌ Listener setup error: ${err?.message || err}`)
      }
    }

    void setupListeners()

    return () => {
      handles.forEach((handle) => {
        handle.remove().catch(() => {})
      })
    }
  }, [addDebugLog, isNative])

  return {
    addDebugLog,
    clearDebugLog,
    debugLog,
    handleTestNative,
    isNative,
    nativePlatform: getNativePlatform(),
  }
}
